import React, { useState, useEffect } from 'react';
import { Form, Button } from 'react-bootstrap';
import Select from 'react-select';
import ApiCaller from '../../../../api/ApiCaller';

const OPERATORS = [
    { value: 'EQUAL_TO', label: 'EQUAL_TO' },
    { value: 'NOT_EQUAL_TO', label: 'NOT_EQUAL_TO' },
    { value: 'GREATER_THAN', label: 'GREATER_THAN' },
    { value: 'LESS_THAN', label: 'LESS_THAN' },
    { value: 'GREATER_THAN_EQUAL_TO', label: 'GREATER_THAN_EQUAL_TO' },
    { value: 'LESS_THAN_EQUAL_TO', label: 'LESS_THAN_EQUAL_TO' },
    { value: 'INCLUDES', label: 'INCLUDES' },
    { value: 'NOT_INCLUDES', label: 'NOT_INCLUDES' },
    { value: 'STARTS_WITH', label: 'STARTS_WITH' },
    { value: 'ENDS_WITH', label: 'ENDS_WITH' },
    { value: 'CONTAINS', label: 'CONTAINS' },
    { value: 'NOT_CONTAINS', label: 'NOT_CONTAINS' }
];

const ConditionAction = ({ 
    formData, 
    setFormData, 
    allTableList, 
    selectedRequest, 
    allPreviousSteps, 
    currentStepId,
    allSteps
}) => {
    const [requestParameters, setRequestParameters] = useState([]);
    const [allRequestParameters, setAllRequestParameters] = useState([]);
    const [requestParamSource, setRequestParamSource] = useState(['custom_request']);
    const [conditionalSteps, setConditionalSteps] = useState([{ condition: '', stepId: '' }]);

    // Effects
    useEffect(() => {
        requestParamSource.forEach(source => {
            if (source === 'custom_request') {
                fetchRequestParameters(selectedRequest);
            } else {
                loadStepParameters(source);
            }
        });
    }, [requestParamSource, selectedRequest, allPreviousSteps]);

    useEffect(() => {
        if (formData?.conditionalSteps) {
            setConditionalSteps(formData.conditionalSteps);
        }
    }, [formData?.conditionalSteps]);

    // Helper Functions
    const fetchRequestParameters = (requestId) => {
        if (!requestId) return;
        
        const url = `api/generic-request/${requestId}/parameters`;
        ApiCaller.callGetApi(url, {}, (response) => {
            const params = response?.data || [];
            const formattedParams = params.map(param => ({
                value: param.name,
                label: `Custom Request - ${param.name}`,
                type: param.type
            }));
            setAllRequestParameters(prev => [...prev, ...formattedParams]);
            setRequestParameters(prev => [...prev, ...formattedParams]);
        }, (error) => {
            console.error('Error fetching request parameters:', error);
        });
    };

    const loadStepParameters = (stepSource) => {
        const stepMatch = stepSource.match(/step_(\d+)/);
        if (!stepMatch) return;

        const stepId = parseInt(stepMatch[1]);
        const step = allPreviousSteps.find(s => s.id === stepId);
        if (!step || !step.actions || step.actions.length === 0) return;

        const action = step.actions[0];
        if (action.config?.responseParameters) {
            const stepParams = action.config.responseParameters.map(param => ({
                value: param.name,
                label: `Step ${stepId} - ${param.name}`,
                type: param.type
            }));
            setAllRequestParameters(prev => [...prev, ...stepParams]);
            setRequestParameters(prev => [...prev, ...stepParams]);
        }
    };

    // Event Handlers
    const handleRequestParamSourceChange = (selectedOptions) => {
        const values = selectedOptions ? selectedOptions?.map(option => option?.value) : ['custom_request'];
        setRequestParamSource(values);
        setFormData((prev) => ({
            ...prev,
            requestParamSource: values,
        }));
    };

    const addConditionalStep = () => {
        const newConditionalSteps = [...conditionalSteps, { condition: '', stepId: '' }];
        setConditionalSteps(newConditionalSteps);
        setFormData(prev => ({
            ...prev,
            conditionalSteps: newConditionalSteps
        }));
    };

    const removeConditionalStep = (index) => {
        const newConditionalSteps = conditionalSteps.filter((_, i) => i !== index);
        setConditionalSteps(newConditionalSteps);
        setFormData(prev => ({
            ...prev,
            conditionalSteps: newConditionalSteps
        }));
    };

    const updateConditionalStep = (index, field, value) => {
        const newConditionalSteps = [...conditionalSteps];
        newConditionalSteps[index] = { ...newConditionalSteps[index], [field]: value };
        
        // Build the condition string
        const step = newConditionalSteps[index];
        if (step.conditionField && step.conditionOperator && step.conditionValue) {
            const fieldLabel = requestParameters.find(param => param.value === step.conditionField)?.label || step.conditionField;
            const operatorLabel = OPERATORS.find(op => op.value === step.conditionOperator)?.label || step.conditionOperator;
            step.condition = `${fieldLabel} ${operatorLabel} ${step.conditionValue}`;
        }
        
        setConditionalSteps(newConditionalSteps);
        setFormData(prev => ({
            ...prev,
            conditionalSteps: newConditionalSteps
        }));
    };

    const renderRequestParameterSource = () => {
        const options = [
            { value: 'custom_request', label: 'Custom Request' },
            ...allPreviousSteps.map(step => ({
                value: `step_${step.id}`,
                label: `Step ${step.id}`
            }))
        ];

        return (
            <Form.Group className="mt-3">
                <Form.Label>Parameter Source</Form.Label>
                <Select
                    isMulti
                    options={options}
                    value={options.filter(option => requestParamSource.includes(option.value))}
                    onChange={handleRequestParamSourceChange}
                    placeholder="Select parameter sources"
                    className="react-select-container"
                />
            </Form.Group>
        );
    };

    const getAvailableSteps = () => {
        if (!allSteps) return [];
        return allSteps
            .filter(step => step.id !== currentStepId)
            .map(step => ({
                value: step.id,
                label: `Step ${step.id}`
            }));
    };

    return (
        <>
            {renderRequestParameterSource()}

            <Form.Group className="mt-3">
                <Form.Label><b>Conditional Steps</b></Form.Label>
                {conditionalSteps.map((conditionalStep, index) => (
                    <div key={index} className="border p-3 mb-3 rounded">
                        <div className="d-flex justify-content-between align-items-center mb-2">
                            <h6>Condition {index + 1}</h6>
                            {conditionalSteps.length > 1 && (
                                <Button 
                                    variant="outline-danger" 
                                    size="sm"
                                    onClick={() => removeConditionalStep(index)}
                                >
                                    Remove
                                </Button>
                            )}
                        </div>
                        
                        <div className="row">
                            <div className="col-md-4">
                                <Form.Label className="small text-muted">Field</Form.Label>
                                <Select
                                    options={requestParameters}
                                    value={requestParameters.find(param => param.value === conditionalStep.conditionField)}
                                    onChange={(selected) => {
                                        updateConditionalStep(index, 'conditionField', selected?.value || '');
                                    }}
                                    placeholder="Select Field"
                                    className="react-select-container"
                                />
                            </div>
                            
                            <div className="col-md-3">
                                <Form.Label className="small text-muted">Operator</Form.Label>
                                <Select
                                    options={OPERATORS}
                                    value={OPERATORS.find(op => op.value === conditionalStep.conditionOperator)}
                                    onChange={(selected) => {
                                        updateConditionalStep(index, 'conditionOperator', selected?.value || '');
                                    }}
                                    placeholder="Select Operator"
                                    className="react-select-container"
                                />
                            </div>
                            
                            <div className="col-md-3">
                                <Form.Label className="small text-muted">Value</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={conditionalStep.conditionValue || ''}
                                    onChange={(e) => {
                                        updateConditionalStep(index, 'conditionValue', e.target.value);
                                    }}
                                    placeholder="Enter Value"
                                />
                            </div>
                            
                            <div className="col-md-2">
                                <Form.Label className="small text-muted">Go to Step</Form.Label>
                                <Select
                                    options={getAvailableSteps()}
                                    value={getAvailableSteps().find(step => step.value === conditionalStep.stepId)}
                                    onChange={(selected) => {
                                        updateConditionalStep(index, 'stepId', selected?.value || '');
                                    }}
                                    placeholder="Select Step"
                                    className="react-select-container"
                                />
                            </div>
                        </div>
                    </div>
                ))}
                
                <Button 
                    variant="outline-primary" 
                    size="sm"
                    onClick={addConditionalStep}
                >
                    + Add Condition
                </Button>
            </Form.Group>

            <Form.Group className="mt-3">
                <Form.Label>Else Step (Optional)</Form.Label>
                <Select
                    options={getAvailableSteps()}
                    value={getAvailableSteps().find(step => step.value === formData?.elseStepId)}
                    onChange={(selected) => {
                        setFormData(prev => ({
                            ...prev,
                            elseStepId: selected?.value || ''
                        }));
                    }}
                    placeholder="Select else step"
                    className="react-select-container"
                    isClearable
                />
            </Form.Group>
        </>
    );
};

export default ConditionAction; 